/**
 * 注册页（架构 §2 src/pages/RegisterPage.tsx / T10 / T03）
 *  - 两步：先填邀请码实时校验（POST /auth/check-invite），再填账号+密码（POST /auth/register）
 *  - 2001 INVITE_INVALID / 2002 INVITE_USED_UP -> 红字
 *  - T03：新增昵称输入 + 头像上传；都不填则前端生成随机昵称、无头像（后端存空，前端兜底）
 */
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import { checkInvite, register } from '../api/auth.api';
import { ApiError } from '../api/client';
import { useAuthStore } from '../store/auth.store';
import { ErrorCode } from '../types/models';
import { generateNickname } from '../utils/nickname';
import AvatarUploader from '../components/profile/AvatarUploader';

export default function RegisterPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const [step, setStep] = useState<1 | 2>(1);
  const [code, setCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [avatar, setAvatar] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!code.trim()) {
      setError('请输入邀请码');
      return;
    }
    setLoading(true);
    try {
      await checkInvite(code.trim());
      setStep(2);
      setInfo('邀请码有效，请设置账号');
    } catch (err) {
      if (
        err instanceof ApiError &&
        (err.code === ErrorCode.INVITE_INVALID || err.code === ErrorCode.INVITE_USED_UP)
      ) {
        setError(err.message || '邀请码无效');
      } else if (err instanceof ApiError) {
        setError(err.message || '校验失败');
      } else {
        setError('网络错误，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  }

  async function doRegister(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (username.trim().length < 2) {
      setError('账号至少 2 个字符');
      return;
    }
    if (password.length < 6) {
      setError('密码至少 6 位');
      return;
    }
    setLoading(true);
    try {
      // 昵称留空 -> 前端兜底生成随机昵称；头像留空 -> 不传（后端存空，前端兜底首字母）
      const finalNick = nickname.trim() || generateNickname();
      const res = await register(
        code.trim(),
        username.trim(),
        password,
        finalNick,
        avatar ?? undefined,
      );
      setAuth(res.token, res.user);
      navigate('/', { replace: true });
    } catch (err) {
      if (
        err instanceof ApiError &&
        (err.code === ErrorCode.INVITE_INVALID || err.code === ErrorCode.INVITE_USED_UP)
      ) {
        setError(err.message || '邀请码无效');
        setStep(1);
      } else if (err instanceof ApiError) {
        setError(err.message || '注册失败');
      } else {
        setError('网络错误，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper sx={{ p: 4, width: 360 }} elevation={0} variant="outlined">
        <Typography variant="h5" align="center" color="primary.main" gutterBottom>
          注册知会
        </Typography>
        {step === 1 ? (
          <Box component="form" onSubmit={verifyCode} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              第一步：输入邀请码
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="邀请码" value={code} onChange={(e) => setCode(e.target.value)} autoFocus fullWidth />
            <Button type="submit" variant="contained" disabled={loading} size="large">
              {loading ? '校验中…' : '校验邀请码'}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={doRegister} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="body2" color="primary.main">
              {info}
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <AvatarUploader value={avatar} onChange={setAvatar} />
            <TextField
              label="昵称"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="选填，留空将自动生成"
              fullWidth
            />
            <TextField label="账号" value={username} onChange={(e) => setUsername(e.target.value)} autoFocus fullWidth />
            <TextField label="密码" type="password" value={password} onChange={(e) => setPassword(e.target.value)} fullWidth />
            <Button type="submit" variant="contained" disabled={loading} size="large">
              {loading ? '注册中…' : '注册并登录'}
            </Button>
            <Button onClick={() => setStep(1)} size="small">
              返回修改邀请码
            </Button>
          </Box>
        )}
        <Typography variant="body2" align="center" sx={{ mt: 2 }}>
          已是创始人？<Link to="/login">直接登录</Link>
        </Typography>
      </Paper>
    </Box>
  );
}
